/**
 * Admin Portal — Client Access Page
 * Manage client organization access and domain visibility.
 * Admins can assign assessment categories and specific sub-domains to client organizations.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, Switch, FormControlLabel, Stack, Accordion,
  AccordionSummary, AccordionDetails, Checkbox, FormGroup, Divider,
} from '@mui/material';
import { Block, CheckCircle, ExpandMore, Assignment } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface ClientOrg {
  id: string;
  organization_name: string;
  organization_domain: string;
  allowed_domains: string[];
  is_active: boolean;
  user_count: number;
}

interface SubDomain {
  name: string;
  domain_id: string;
  assessment_id: string;
}

interface CategoryDomain {
  name: string;
  sub_domains: SubDomain[];
}

const ClientAccess: React.FC = () => {
  const [clients, setClients] = useState<ClientOrg[]>([]);
  const [categories, setCategories] = useState<CategoryDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [editClient, setEditClient] = useState<ClientOrg | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchClientAccessData = useCallback(() => {

    setLoading(true);
    Promise.all([
      authFetch('/api/admin/organizations').then((r) => r.json()),
      authFetch('/api/admin/assessment-domains').then((r) => r.json()),
    ])
      .then(([orgData, domainData]) => {
        const orgs = (orgData.organizations || []).map((o: any) => ({
          id: o.id,
          organization_name: o.name || o.organization_name || '—',
          organization_domain: o.domain || o.organization_domain || '—',
          allowed_domains: o.allowed_domains || [],
          is_active: o.is_active !== undefined ? o.is_active : true,
          user_count: o.user_count || 0,
        }));
        setClients(orgs);
        if (domainData.success) setCategories(domainData.categories || []);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });

  }, []);

  useEffect(() => { fetchClientAccessData(); }, [fetchClientAccessData]);

  const openEditDialog = (client: ClientOrg) => {
    setEditClient(client);
    setSelectedCategories(new Set(client.allowed_domains || []));
    setIsActive(client.is_active);
    setError('');
    setSuccess('');
  };

  const handleCategoryToggle = (categoryName: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedCategories.size === categories.length) {
      setSelectedCategories(new Set());
    } else {
      setSelectedCategories(new Set(categories.map((c) => c.name)));
    }
  };

  const handleSave = async () => {
    if (!editClient) return;
    try {
      const res = await authFetch(`/api/admin/organizations/${editClient.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allowed_domains: Array.from(selectedCategories),
          is_active: isActive,
        }),
      });
      if (!res.ok) throw new Error('Failed to update client');
      setClients((prev) =>
        prev.map((c) =>
          c.id === editClient.id
            ? { ...c, allowed_domains: Array.from(selectedCategories), is_active: isActive }
            : c
        )
      );
      setSuccess(`Assessment domains updated for ${editClient.organization_name}`);
      setTimeout(() => { setEditClient(null); setSuccess(''); }, 1500);
    } catch {
      setError('Failed to update client access. Check permissions.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Client Access Management
        </Typography>
        <RefreshButton onRefresh={fetchClientAccessData} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control which assessment domains each client organization can access.
        Clients will only see categories assigned here when starting an assessment.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Users</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned Assessment Domains</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}>Loading...</TableCell></TableRow>
            ) : clients.length === 0 ? (
              <TableRow><TableCell colSpan={6}>No client organizations found</TableCell></TableRow>
            ) : (
              clients.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{c.organization_name}</TableCell>
                  <TableCell>{c.organization_domain}</TableCell>
                  <TableCell>{c.user_count}</TableCell>
                  <TableCell>
                    {(c.allowed_domains || []).length === 0 ? (
                      <Chip label="All domains (unrestricted)" size="small" color="warning" variant="outlined" />
                    ) : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(c.allowed_domains || []).map((d) => (
                          <Chip key={d} label={d} size="small" color="primary" variant="outlined" />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.is_active ? (
                      <Chip icon={<CheckCircle />} label="Active" size="small" color="success" />
                    ) : (
                      <Chip icon={<Block />} label="Disabled" size="small" color="error" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEditDialog(c)} title="Assign assessment domains">
                      <Assignment fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Edit Client Access Dialog */}
      <Dialog open={!!editClient} onClose={() => setEditClient(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Assessment Domains — {editClient?.organization_name}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which assessment categories this client organization can access.
            If no categories are selected, the client can access all domains (unrestricted).
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button size="small" onClick={handleSelectAll}>
              {selectedCategories.size === categories.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Chip
              label={`${selectedCategories.size} of ${categories.length} selected`}
              size="small"
              color={selectedCategories.size > 0 ? 'primary' : 'default'}
            />
          </Box>

          {categories.map((cat) => (
            <Accordion key={cat.name} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCategories.has(cat.name)}
                      onChange={() => handleCategoryToggle(cat.name)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 500 }}>{cat.name}</Typography>}
                  onClick={(e) => e.stopPropagation()}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ pl: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Sub-domains included in this category:
                </Typography>
                <FormGroup>
                  {cat.sub_domains.map((sub) => (
                    <Typography key={sub.domain_id} variant="body2" sx={{ py: 0.3, pl: 1 }}>
                      • {sub.name}
                    </Typography>
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          ))}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Organization Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditClient(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAccess;
